'use client';

import { useRef, type ForwardedRef, type ReactNode } from 'react';
import { forwardRef } from '../../forward-ref.js';
import { useLocalizedMessages } from '../../i18n/localization-provider.js';
import { Spinner } from '../spinner/spinner.js';
import type { CommonProps } from '../types.js';
import { useFullWidthCell } from './contexts/column-collection.js';
import { useTableLoading } from './contexts/loading.js';

export interface TableBodyProps extends CommonProps {
  /** The rows that belong to this table body. */
  children: ReactNode;
}

function TableBody(
  { children, UNSAFE_className, UNSAFE_style, ...props }: TableBodyProps,
  ref: ForwardedRef<HTMLTableSectionElement>,
) {
  const isLoading = useTableLoading();

  return (
    <tbody {...props} ref={ref} className={UNSAFE_className} style={UNSAFE_style}>
      {isLoading && <TableLoader />}
      {children}
    </tbody>
  );
}

/**
 * Renders a body section within a table.
 *
 * See [table usage guidelines](https://ui.cimpress.io/components/table/) and [table building guide](https://ui.cimpress.io/dev-guides/tables/).
 */
const _TableBody = forwardRef(TableBody, 'TableBody');

export { _TableBody as TableBody };

function TableLoader() {
  const loaderCellRef = useRef<HTMLTableCellElement>(null);
  const { colSpan } = useFullWidthCell(loaderCellRef);
  const messages = useLocalizedMessages('table');

  return (
    <tr key="__cim__internal__loading-state__">
      <td ref={loaderCellRef} className="cim-table-loading-state" colSpan={colSpan} data-alignment="center">
        <div className="cim-table-loading-state-overlay">
          <Spinner
            size="medium"
            aria-label={messages.format('loadingData')}
            UNSAFE_style={{ height: 'min(70%, 48px)' }}
          />
        </div>
      </td>
    </tr>
  );
}
